import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { UserSession } from '@/types/sessions';
import type { Json } from '@/types/database.generated';

export const userSessionsQueryKey = (userId: string | null | undefined) =>
  ['user-sessions', userId] as const;

/**
 * Fetch all active sessions for the current user
 */
export function useUserSessions(userId: string | null | undefined) {
  return useQuery({
    queryKey: userSessionsQueryKey(userId),
    enabled: Boolean(userId),
    queryFn: async (): Promise<UserSession[]> => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('is_revoked', false)
        .order('last_activity_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as UserSession[];
    },
    staleTime: 30_000, // Refresh every 30 seconds
    refetchInterval: 30_000, // Auto-refresh
  });
}

/**
 * Revoke a specific session
 */
export function useRevokeSession() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      reason,
    }: {
      sessionId: string;
      reason?: string;
    }): Promise<boolean> => {
      const { data, error } = await supabase.rpc('revoke_user_session', {
        p_session_id: sessionId,
        p_reason: reason,
      });

      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: () => {
      // Invalidate sessions query to refetch
      void qc.invalidateQueries({ queryKey: ['user-sessions'] });
    },
  });
}

/**
 * Revoke all other sessions except the current one
 */
export function useRevokeOtherSessions() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (currentSessionToken: string): Promise<number> => {
      const { data, error } = await supabase.rpc('revoke_other_sessions', {
        p_current_session_token: currentSessionToken,
      });

      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-sessions'] });
    },
  });
}

/**
 * Trust a specific session (remember device)
 */
export function useTrustSession() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      durationDays = 30,
    }: {
      sessionId: string;
      durationDays?: number;
    }): Promise<boolean> => {
      const { data, error } = await supabase.rpc('trust_user_session', {
        p_session_id: sessionId,
        p_duration_days: durationDays,
      });

      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-sessions'] });
    },
  });
}

/**
 * Create or update session on login
 */
export function useUpsertSession() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionToken,
      refreshTokenHash,
      deviceInfo,
      expiresAt,
    }: {
      sessionToken: string;
      refreshTokenHash: string;
      deviceInfo: Record<string, unknown>;
      expiresAt: string;
    }): Promise<UserSession> => {
      const { data, error } = await supabase.rpc('upsert_user_session', {
        p_session_token: sessionToken,
        p_refresh_token_hash: refreshTokenHash,
        p_device_info: deviceInfo as Json,
        p_expires_at: expiresAt,
      });

      if (error) throw error;
      return data as unknown as UserSession;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-sessions'] });
    },
  });
}
